import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { exportTableToPDF } from "./utils/exportPDF";

const PurchaseList = () => {
  const [kitchenId, setKitchenId] = useState("");
  const [requisitionList, setRequisitionList] = useState([]);
  const [filteredList, setFilteredList] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [startDateFilter, setStartDateFilter] = useState("");
  const [endDateFilter, setEndDateFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (user?.kitchen) {
      setKitchenId(user.kitchen);
    }
  }, []);

  // ✅ Fetch API Data
  const fetchRequisitionList = async () => {
    if (!kitchenId) return;
    try {
      const response = await axios.get(
        `https://api-ims.indevconsultancy.in/requisition_export_report.php?kitchen_id=${kitchenId}`
      );
      if (response.data.success) {
        setRequisitionList(response.data.data);
        setFilteredList(response.data.data);
      } else {
        setRequisitionList([]);
        setFilteredList([]);
      }
    } catch (error) {
      console.error("Error fetching requisition data:", error);
    }
  };

  // ✅ Fetch when kitchenId changes
  useEffect(() => {
    if (kitchenId) fetchRequisitionList();
  }, [kitchenId]);

  // ✅ Filter by search term and date range
  useEffect(() => {
    let filtered = [...requisitionList];

    // ✅ Date Filter
    if (startDateFilter && endDateFilter) {
      filtered = filtered.filter((item) => {
        const date = new Date(item.required_date);
        return (
          date >= new Date(startDateFilter) && date <= new Date(endDateFilter)
        );
      });
    }

    // ✅ Universal Search (check all fields)
    if (searchTerm.trim() !== "") {
      const lowerSearch = searchTerm.toLowerCase();
      filtered = filtered.filter((item) =>
        Object.values(item).some((val) =>
          String(val || "").toLowerCase().includes(lowerSearch)
        )
      );
    }

    setFilteredList(filtered);
    setCurrentPage(1);
  }, [searchTerm, startDateFilter, endDateFilter, requisitionList]);

  // ✅ Pagination
  const totalPages = Math.ceil(filteredList.length / itemsPerPage);
  const paginatedList = filteredList.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // ✅ Export CSV
  const handleExportCSV = () => {
    if (filteredList.length === 0) return alert("No data to export.");

    const csvHeader = Object.keys(filteredList[0]).join(",");
    const csvRows = filteredList.map((item) =>
      Object.values(item)
        .map((v) => `"${v || ""}"`)
        .join(",")
    );

    const csvContent = [csvHeader, ...csvRows].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "requisition_report.csv";
    a.click();
  };

  const handleExportPDF = () => {
    if (filteredList.length === 0) return alert("No data to export.");
    const headers = Object.keys(filteredList[0]);
    const rows = filteredList.map((item) => headers.map((h) => item[h] || ""));
    exportTableToPDF({ title: "Requisition Report", headers, rows, filename: "requisition_report.pdf", filterInfo: `Total: ${rows.length}` });
  };

  return (
    <div className="container-fluid px-2">
      <div className="content">
        <div className="container-fluid">
          {/* Header */}
          <div className="row align-items-center justify-content-between py-2">
            <div className="col-md-6 col-6">
              <nav aria-label="breadcrumb">
                <ol className="breadcrumb justify-content-start mb-0">
                  <li className="breadcrumb-item">
                    <Link to="/mobilehome">Home</Link>
                  </li>
                </ol>
              </nav>
            </div>
            <div className="col-md-6 col-6 text-end">
              <span className="fs-1">Requisition List</span>
            </div>
          </div>

          {/* Filters */}
          <div className="card mb-3">
            <div className="card-header border-info">
              <div className="row g-3 align-items-end">
                <div className="col-lg-4">
                  <label className="form-label">Search (All Fields)</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search by ID, Vendor, Category, etc."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>

                <div className="col-lg-3">
                  <label className="form-label">Start Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={startDateFilter}
                    onChange={(e) => setStartDateFilter(e.target.value)}
                  />
                </div>

                <div className="col-lg-3">
                  <label className="form-label">End Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={endDateFilter}
                    onChange={(e) => setEndDateFilter(e.target.value)}
                  />
                </div>

                <div className="col-lg-2 text-end">
                  <button
                    type="button"
                    className="btn btn-outline-dark w-100"
                    onClick={() => {
                      setSearchTerm("");
                      setStartDateFilter("");
                      setEndDateFilter("");
                      setFilteredList(requisitionList);
                    }}
                  >
                    Reset
                  </button>
                </div>
              </div>
            </div>

            {/* Table */}
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6>
                  Total Requisition(s): <span>{filteredList.length}</span>
                </h6>
                <div>
                  <button className="btn btn-primary btn-sm me-1" onClick={handleExportCSV}>
                    <i className="fas fa-file-csv me-1"></i> Export CSV
                  </button>
                  <button className="btn btn-danger btn-sm" onClick={handleExportPDF}>
                    <i className="fas fa-file-pdf me-1"></i> Export PDF
                  </button>
                </div>
              </div>

              <div className="table-responsive scrollbar">
                <table className="table table-bordered text-center table-striped fs--1 mb-0">
                  <thead className="bg-primary text-white">
                    <tr>
                      <th>#</th>
                      <th>Req. ID</th>
                      <th>Category</th>
                      <th>Stock Name</th>
                      <th>Vendor</th>
                      <th>Requested Qty</th>
                      <th>Received Qty</th>
                      <th>Required Date</th>
                      <th>Approved Qty</th>
                      <th>Pending Qty</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedList.length === 0 ? (
                      <tr>
                        <td colSpan="10">Loading or no data available</td>
                      </tr>
                    ) : (
                      paginatedList.map((item, index) => (
                        <tr key={index}>
                          <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                          <td>{item.reqid}</td>
                          <td>{item.category_name}</td>
                          <td>{item.stock_name}</td>
                          <td>{item.vendor_name?.trim()}</td>
                          <td>{item.requestquantity}</td>
                          <td>{item.recievedquantity}</td>
                          <td>
                            {new Date(item.required_date).toLocaleDateString()}
                          </td>
                          <td>{item.approvequantity}</td>
                          <td>{item.pendingquantity}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {filteredList.length > itemsPerPage && (
                <div className="d-flex justify-content-between align-items-center mt-3">
                  <button
                    className="btn btn-outline-primary btn-sm"
                    onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </button>
                  <span>
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    className="btn btn-outline-primary btn-sm"
                    onClick={() =>
                      setCurrentPage((p) => Math.min(p + 1, totalPages))
                    }
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PurchaseList;
